import { useEffect, useRef, useState } from 'react';

const zones = [
  'Pereira centro',
  'Cuba y Boston',
  'Pinares',
  'Álamos',
  'Dosquebradas centro',
  'Frailes',
];

export default function Cobertura() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="cobertura"
      ref={ref}
      className="bg-gradient-to-b from-md-light to-white py-[72px] md:py-24"
    >
      <div className="mx-auto flex max-w-[1200px] flex-col gap-12 px-6 md:flex-row md:items-center md:gap-16 md:px-12">
        {/* Left Column */}
        <div className="flex-1">
          <h2 className="mb-8 font-serif text-[28px] font-bold leading-[1.2] text-md-ink md:text-[38px]">
            Llevamos tu domicilio en Pereira y Dosquebradas
          </h2>

          <div className="mb-10 grid grid-cols-2 gap-3 md:gap-x-3 md:gap-y-4">
            {zones.map((zone, i) => (
              <span
                key={zone}
                className={`flex items-center gap-2.5 rounded-full border-[1.5px] border-md-sky bg-white px-5 py-3.5 text-[14px] font-medium text-md-ink transition-colors hover:border-md-blue md:text-[15px] ${
                  visible ? 'animate-fade-in-up' : 'opacity-0'
                }`}
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <span className="flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center text-md-teal">
                  <i className="ri-map-pin-line" />
                </span>
                {zone}
              </span>
            ))}
          </div>

          <a
            href="tel:+573206789030"
            className="inline-flex items-center gap-2 rounded-xl border-2 border-md-dark px-7 py-3.5 text-[15px] font-semibold text-md-dark transition-all hover:bg-md-dark/5 md:text-[16px]"
            onClick={() => {
              if (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).mdCallClick) {
                ((window as unknown as Record<string, unknown>).mdCallClick as () => void)();
              }
            }}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-map-pin-2-line" />
            </span>
            Consulta tu zona
          </a>
        </div>

        {/* Right Column - Decorative Map SVG */}
        <div className="flex-1">
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl bg-md-light">
            {/* Grid background */}
            <svg
              className="absolute inset-0 h-full w-full"
              viewBox="0 0 400 300"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path
                    d="M 40 0 L 0 0 0 40"
                    fill="none"
                    stroke="#E5EFF8"
                    strokeWidth="1"
                  />
                </pattern>
                <radialGradient id="haloPereira" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#1F6FB8" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#1F6FB8" stopOpacity="0" />
                </radialGradient>
                <radialGradient id="haloDosquebradas" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#14A085" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#14A085" stopOpacity="0" />
                </radialGradient>
              </defs>
              <rect width="400" height="300" fill="url(#grid)" />

              {/* Pereira halo */}
              <circle cx="160" cy="150" r="90" fill="url(#haloPereira)" />
              <circle cx="160" cy="150" r="6" fill="#1F6FB8" />
              <text x="160" y="120" textAnchor="middle" fontSize="13" fontWeight="600" fill="#1F6FB8" fontFamily="Plus Jakarta Sans, sans-serif">
                Pereira
              </text>

              {/* Dosquebradas halo */}
              <circle cx="280" cy="150" r="70" fill="url(#haloDosquebradas)" />
              <circle cx="280" cy="150" r="5" fill="#14A085" />
              <text x="280" y="120" textAnchor="middle" fontSize="13" fontWeight="600" fill="#0E8F75" fontFamily="Plus Jakarta Sans, sans-serif">
                Dosquebradas
              </text>

              {/* Route line */}
              <path
                d="M 166 150 Q 220 120 274 150"
                fill="none"
                stroke="#1F6FB8"
                strokeWidth="2"
                strokeDasharray="6 4"
                opacity="0.6"
              />

              {/* Truck icon */}
              <g transform="translate(215, 132)">
                <rect x="0" y="0" width="20" height="14" rx="3" fill="#1F6FB8" />
                <rect x="14" y="3" width="5" height="8" rx="1" fill="white" />
                <circle cx="5" cy="15" r="2.5" fill="#0F1F35" />
                <circle cx="15" cy="15" r="2.5" fill="#0F1F35" />
              </g>

              {/* Decorative pins */}
              <circle cx="100" cy="80" r="3" fill="#14A085" opacity="0.5" />
              <circle cx="340" cy="200" r="3" fill="#1F6FB8" opacity="0.5" />
              <circle cx="80" cy="220" r="3" fill="#14A085" opacity="0.5" />
              <circle cx="360" cy="100" r="3" fill="#1F6FB8" opacity="0.5" />
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}